import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Zap, Shield, BarChart2, Brain } from "lucide-react";
import SEOMeta from "@/components/SEOMeta";
import ExitIntent from "@/components/ExitIntent";

const FEATURES = [
  { icon: BarChart2, title: "Visual Strategy Builder", desc: "Build algo strategies without code. VWAP, EMA, RSI, breakout — configure in plain English." },
  { icon: Zap, title: "Bracket Orders + Trailing SL", desc: "Automated stop-loss and target execution. The system manages your trade so you don't have to watch the screen." },
  { icon: Brain, title: "AI Copilot", desc: "Explain your strategy, find edge cases, optimize parameters — ask in plain Hindi or English." },
  { icon: Shield, title: "Risk Management Built-in", desc: "Daily loss cap, max drawdown limit, kill switch. Trading halts automatically when your limits are hit." },
  { icon: BarChart2, title: "Options Intelligence", desc: "Live IV Rank, PCR, Max Pain, full Greeks chain for Nifty and BankNifty. Know the sentiment before you trade." },
  { icon: CheckCircle, title: "₹10 Lakh Paper Trading", desc: "Test any strategy with virtual capital at real market prices. No risk, full realism." },
];

const SOCIAL_PROOF = [
  { stat: "₹10L", label: "Virtual capital, free" },
  { stat: "8+", label: "Options strategy templates" },
  { stat: "3", label: "Brokers supported" },
  { stat: "24/7", label: "Automated monitoring" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-white">
      <SEOMeta
        title="AlgoForge — Algo Trading Platform for Indian Retail Traders"
        description="Automate your trading strategies on Zerodha, Dhan & Upstox. Free paper trading, options intelligence, bracket orders, and AI copilot. SEBI compliant."
        url="/about"
        keywords="algo trading platform india, automated trading india, zerodha algo trading, options trading platform india, paper trading india free"
      />
      <ExitIntent />

      {/* Nav */}
      <nav className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between sticky top-0 bg-[var(--bg-page)]/95 backdrop-blur z-40">
        <Link to="/" className="font-section text-lg tracking-widest text-white">ALGOFORGE</Link>
        <div className="flex items-center gap-6 text-xs font-section tracking-wider txt-muted">
          <Link to="/learn" className="hover:text-white transition-colors hidden sm:block">LEARN</Link>
          <Link to="/blog" className="hover:text-white transition-colors hidden sm:block">BLOG</Link>
          <Link to="/faq" className="hover:text-white transition-colors hidden sm:block">FAQ</Link>
          <Link to="/pricing" className="hover:text-white transition-colors hidden sm:block">PRICING</Link>
          <Link to="/login" className="hover:text-white transition-colors">LOGIN</Link>
          <Link to="/register" className="px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-muted)] transition-colors">
            START FREE
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
        <div className="absolute inset-0 -top-16 glow-accent pointer-events-none" />
        <div className="inline-block text-xs font-section tracking-widest txt-accent border border-[var(--accent)]/30 px-3 py-1 mb-6">
          SEBI COMPLIANT · NOT SEBI REGISTERED
        </div>
        <h1 className="font-section text-4xl sm:text-5xl text-white leading-tight mb-5">
          Automate Your Trading.<br />
          <span className="txt-accent">Without Writing Code.</span>
        </h1>
        <p className="txt-secondary text-base sm:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
          AlgoForge is an algo trading cockpit for Indian retail traders. Build strategies visually,
          backtest on NSE data, paper trade with ₹10 lakh virtual capital, and automate execution
          on Zerodha, Dhan, or Upstox.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[var(--accent)] hover:bg-indigo-500 text-white font-section tracking-wider transition-colors"
          >
            Start Free — No Credit Card <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/learn"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-[var(--border)] txt-muted hover:border-white hover:text-white font-section tracking-wider transition-colors"
          >
            Read the Guides
          </Link>
        </div>
        <p className="txt-muted text-xs mt-4">Free plan includes paper trading, signal feed, watchlist, price alerts, and strategy builder.</p>
      </section>

      {/* Stats */}
      <section className="border-y border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          {SOCIAL_PROOF.map((s) => (
            <div key={s.stat}>
              <div className="font-section text-3xl text-white mb-1">{s.stat}</div>
              <div className="txt-muted text-xs">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="overline txt-accent text-center mb-3">Platform</div>
        <h2 className="font-section text-2xl text-white text-center mb-12">Everything you need. Nothing you don't.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="panel p-5">
              <f.icon className="w-5 h-5 txt-accent mb-3" />
              <div className="font-section text-sm text-white mb-2">{f.title}</div>
              <div className="txt-secondary text-xs leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-t border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="overline txt-accent mb-3">Built For</div>
          <h2 className="font-section text-2xl text-white mb-6">Indian retail traders who are tired of manual execution</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left mb-10">
            {[
              { who: "Options Traders", pain: "Track IV Rank, PCR, Max Pain, and run payoff diagrams before entering any position." },
              { who: "Intraday Traders", pain: "Automate entries and exits. Bracket orders with trailing SL so you don't sit at the screen all day." },
              { who: "Strategy Builders", pain: "Backtest on NSE historical data, paper trade to validate, then go live with full automation." },
            ].map((c) => (
              <div key={c.who} className="panel p-4">
                <div className="font-section text-sm text-white mb-2">{c.who}</div>
                <div className="txt-secondary text-xs leading-relaxed">{c.pain}</div>
              </div>
            ))}
          </div>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[var(--accent)] hover:bg-indigo-500 text-white font-section tracking-wider transition-colors"
          >
            Start Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Free vs Pro */}
      <section className="border-t border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="overline text-center txt-accent mb-3">Pricing</div>
          <h2 className="font-section text-2xl text-white text-center mb-10">Start free. Upgrade when you're ready.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                name: "Free", price: "₹0", desc: "Everything to learn and practice",
                features: ["₹10L paper trading", "Strategy builder + backtest", "Signal feed", "Watchlist + price alerts", "FAQ & knowledge base", "Daily market blog"],
              },
              {
                name: "Pro", price: "₹999/mo", desc: "For traders going live",
                features: ["Everything in Free", "AI Copilot", "Bracket orders + trailing SL", "Full options intelligence (IV Rank, PCR, Max Pain, Greeks)", "Live broker execution", "Priority support"],
                highlight: true,
              },
            ].map((p) => (
              <div key={p.name} className={`panel p-6 ${p.highlight ? "border-[var(--accent)]/40" : ""}`}>
                <div className="overline text-xs mb-1 txt-accent">{p.name}</div>
                <div className="font-section text-2xl text-white mb-1">{p.price}</div>
                <div className="txt-muted text-xs mb-5">{p.desc}</div>
                <ul className="space-y-2 mb-6">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs txt-secondary">
                      <CheckCircle className="w-3.5 h-3.5 txt-accent flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`block text-center px-4 py-2.5 font-section tracking-wider text-xs transition-colors ${
                    p.highlight
                      ? "bg-[var(--accent)] hover:bg-indigo-500 text-white"
                      : "border border-[var(--border)] txt-muted hover:border-white hover:text-white"
                  }`}
                >
                  {p.highlight ? "Start Pro Trial" : "Start Free"}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn links */}
      <section className="border-t border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <div className="overline text-center txt-accent mb-8">Free Resources</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: "How to automate Zerodha trading", href: "/learn/how-to-automate-zerodha-trading" },
              { label: "IV Rank explained for Indian traders", href: "/learn/what-is-iv-rank-options-india" },
              { label: "Algo trading without coding — 2025 guide", href: "/learn/algo-trading-india-without-coding" },
              { label: "Iron Condor strategy guide", href: "/strategy-guide/iron-condor-nifty" },
              { label: "Nifty expiry strategy", href: "/learn/nifty-options-expiry-strategy" },
              { label: "Paper trading — why and how", href: "/learn/what-is-paper-trading-india" },
            ].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="text-xs txt-secondary hover:txt-accent transition-colors flex items-center gap-1.5 py-2 border-b border-[var(--border)]"
              >
                <ArrowRight className="w-3 h-3 flex-shrink-0" /> {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--border)] px-6 py-8">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-section text-sm tracking-widest txt-muted">ALGOFORGE</span>
          <div className="flex gap-6 text-xs txt-muted">
            <Link to="/learn" className="hover:text-white transition-colors">Learn</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-white transition-colors">Login</Link>
          </div>
          <div className="text-xs txt-muted text-center sm:text-right">
            Technology platform. Not SEBI registered.<br />
            <a href="https://t.me/AlgoForgeIndia" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Telegram</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
